import backendConfig from "../config/backendConfig.ts";
import { ProductImages } from "../models/product.ts";

// Helper function to upload a single image to the backend
// Interface for image upload data that can contain File objects or URLs
interface ImageUploadData {
  front?: string | File;
  back?: string | File;
  detail?: (string | File)[];
}

const uploadImage = async (file: File) => {
  if (!file) return null;

  const formData = new FormData();
  formData.append('image', file);

  try {
    const response = await fetch(`${backendConfig.API_BASE_URL}${backendConfig.ENDPOINTS.UPLOAD}/single`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log("imageData", result);
    
    if (!result.success) {
      throw new Error(result.message || 'Upload failed');
    }
    
    return { publicUrl: result.url };
  } catch (error) {
    console.log("imageError", error);
    return null; // Return null instead of the error to maintain consistent return type
  }
};

export const multipleImageUploads = async (
  data: ImageUploadData
): Promise<ProductImages> => {
  const images: ProductImages = {
    front: "",
    back: "",
    detail: [],
  };
  
  // Ensure images.detail is always an array
  if (!Array.isArray(images.detail)) {
    images.detail = [];
  }

  // Handle front image - could be a File object or string URL
  if (data.front) {
    if (typeof data.front === 'string') {
      images.front = data.front;
    } else if (data.front instanceof File) {
      const frontImage = await uploadImage(data.front);
      if (frontImage && 'publicUrl' in frontImage) {
        images.front = frontImage.publicUrl || "";
      } else {
        images.front = ""; // Set to empty string if upload failed
      }
    }
  }

  // Handle back image - could be a File object or string URL
  if (data.back) {
    if (typeof data.back === 'string') {
      images.back = data.back;
    } else if (data.back instanceof File) {
      const backImage = await uploadImage(data.back);
      if (backImage && 'publicUrl' in backImage) {
        images.back = backImage.publicUrl || "";
      } else {
        images.back = ""; // Set to empty string if upload failed
      }
    }
  }

  // Handle detail images - could be File objects or string URLs
  if (data.detail && Array.isArray(data.detail)) {
    for (const item of data.detail) {
      if (typeof item === 'string') {
        images.detail.push(item);
      } else if (item instanceof File) {
        const img = await uploadImage(item);
        if (img && 'publicUrl' in img) {
          images.detail.push(img.publicUrl);
        }
        // Skip adding anything to detail array if upload failed
      }
    }
  }

  return images;
};

export const imagesService = { multipleImageUploads, uploadImage };
